/**
 * Telegram recipients — /api/v1/control/telegram/*, tenant-scoped by X-Company-Id.
 *
 * Scope ids are CONTROL-NATIVE and that matters: a department scope must carry
 * sv_control's departments.department_id (GET /control/departments), never
 * sv_organization's department id, because sv_control matches subscriptions
 * against the person's control department ids. An org id would bind cleanly and
 * then never deliver a single message.
 */
import {requestControl} from '@/shared/api';
import type {
  CreateInviteInput,
  CreateInviteResult,
  TelegramRecipients,
} from '@/entities/telegram/types';

export function fetchTelegramRecipients(companyId: string): Promise<TelegramRecipients> {
  return requestControl<TelegramRecipients>('/control/telegram/recipients', companyId);
}

export function createTelegramInvite(
  companyId: string,
  input: CreateInviteInput,
): Promise<CreateInviteResult> {
  return requestControl<CreateInviteResult>('/control/telegram/invites', companyId, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function updateTelegramSubscription(
  companyId: string,
  id: string,
  toggles: {
    notify_arrival: boolean;
    notify_departure: boolean;
    include_in_digest: boolean;
  },
): Promise<unknown> {
  return requestControl<unknown>(
    `/control/telegram/subscriptions/${encodeURIComponent(id)}`,
    companyId,
    {method: 'PATCH', body: JSON.stringify(toggles)},
  );
}

export function deleteTelegramSubscription(companyId: string, id: string): Promise<unknown> {
  return requestControl<unknown>(
    `/control/telegram/subscriptions/${encodeURIComponent(id)}`,
    companyId,
    {method: 'DELETE'},
  );
}

export function revokeTelegramInvite(companyId: string, code: string): Promise<unknown> {
  return requestControl<unknown>(
    `/control/telegram/invites/${encodeURIComponent(code)}`,
    companyId,
    {method: 'DELETE'},
  );
}
